import { useEffect, useLayoutEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import {
  QuestionnaireResponse,
  StorePreferenceCreateRequest,
} from '@tiendanube/common';
import { useNavTabs } from 'App/components/Navtabs';
import { IonPageStratus, useResponsive } from 'commons/components';
import useStorage from 'commons/hooks/useStorage';
import { useGetStoreInfo } from 'domains/Auth/hooks';
import { AppLoading } from 'domains/PartnersApps/components/EmbeddedApp';
import { QuestionnaireForm } from './components';
import ImageSrc from './images/new-register-img.png';
import { useGetQuestionnaireOptionsQuery } from './preOnboardingApi';
import { useQuestionnaireLogic } from './useQuestionnaireLogic';
import './QuestionnairePage.scss';

const DEFAULT_QUESTIONNAIRE_RESPONSE: QuestionnaireResponse = { options: [] };

interface QuestionnairePageProps {
  onError: () => void;
  onCompleted: (payload: StorePreferenceCreateRequest) => Promise<void>;
}

function QuestionnairePage({
  onError,
  onCompleted,
}: Readonly<QuestionnairePageProps>) {
  const { id, country } = useGetStoreInfo();
  const [questionnaireCompleted, , completedLoaded] = useStorage(
    `pre-onboarding-questionnaire-completed-${id}`,
    false,
  );
  const {
    data: response,
    isLoading,
    isError,
    isUninitialized,
  } = useGetQuestionnaireOptionsQuery(undefined, {
    skip: questionnaireCompleted === true || !completedLoaded,
  });

  const { isMobile } = useResponsive();
  const { hideSideBar, hideNavTabs, showNavTabs } = useNavTabs();
  const isQuestionnaireRevamp = country === 'BR' || country === 'MX';

  useLayoutEffect(() => {
    hideSideBar();
    hideNavTabs();
    return () => showNavTabs();
  }, [hideSideBar, hideNavTabs, showNavTabs]);

  useEffect(() => {
    if (!isUninitialized && !isLoading && (!response || isError)) onError();
  }, [response, isError, onError, isUninitialized, isLoading]);

  const {
    state,
    currentGroup,
    currentValues,
    isCheckbox,
    isLastStep,
    transformedTypes,
    hasSelectedEcommerce,
    setValues,
    handleSubmit,
    handleSkipGroup,
    handlePreviousStep,
  } = useQuestionnaireLogic({
    response: response ?? DEFAULT_QUESTIONNAIRE_RESPONSE,
    onCompleted,
  });

  if (isLoading || !response || isError || !completedLoaded || !currentGroup)
    return <AppLoading />;

  return (
    <IonPageStratus width="large">
      <Box
        display="flex"
        flexDirection="row"
        width="100vw"
        height="100vh"
        overflow="hidden"
        position="fixed"
        top="0"
        left="0"
        backgroundColor="neutral-background"
      >
        {!isMobile && (
          <div
            className={`stratus--questionnaire-page-wrapper ${
              isQuestionnaireRevamp ? 'stratus--no-border' : ''
            }`}
          >
            <img
              src={ImageSrc}
              alt="questionnaire"
              width="100%"
              height="100%"
            />
          </div>
        )}
        <QuestionnaireForm
          values={currentValues}
          setValues={setValues}
          onSubmit={handleSubmit}
          onSkipGroup={handleSkipGroup}
          onReturn={state.currentStep > 0 ? handlePreviousStep : undefined}
          currentGroup={currentGroup.group}
          isCheckbox={isCheckbox}
          questionnaireOptions={transformedTypes}
          isLastStep={isLastStep}
          isQuestionnaireRevamp={isQuestionnaireRevamp}
          category={state.currentStepCategory}
          hasSelectedEcommerce={hasSelectedEcommerce}
        />
      </Box>
    </IonPageStratus>
  );
}

export default QuestionnairePage;
